<script setup lang="ts">
import { computed, ref } from 'vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import Tag from '../Tag/Tag.vue';

export type Props = {
  type: 'person' | 'company';
  media?: {
    srcSet?: {
      mobile: string;
      tablet: string;
      desktop: string;
      original: string;
    };
    src: string;
    alt: string;
  };
  name?: string;
  company?: string;
  description?: string;
  position?: string;
  mobile?: string;
  tel?: string;
  fax?: string;
  email?: string;
  website?: string;
  websiteText?: string;
  vCard?: string;
  address?: string;
  street?: string;
  city?: string;
  country?: string;
  countryCode?: string;
  additionalText?: string;
  isCollapsible?: boolean;
  isGray?: boolean;
  hasMorelInfo?: boolean;
  jobOpeningsUrl?: string;
};

const props = defineProps<Props>();

// TODO: connect translation service
const openLabel = ref<string>('Show all contact options');
const closeLabel = ref<string>('Hide contact options');
const googleMapsLabel = ref<string>('Check in GoogleMaps');
const moreInfoLabel = ref<string>('More information');
const jobOpeningsLabel = ref<string>('Job openings');

const showCollapsible = ref<boolean>(!!props.isCollapsible);

const collapsed = ref<boolean>(showCollapsible.value ? true : false);

const googleMapsUrl = computed(() => {
  if (!props.address) {
    return;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    props.address + ',' + props.country,
  )}`;
});

const openMoreInfo = () => {
  console.log('XXX should show more info');
};
</script>
<template>
  <div :class="[$style.container, isGray && $style.dark]">
    <template v-if="type === 'company'">
      <Tag
        v-if="name"
        :aria-label="name"
        :class="$style.tag"
        color="grey"
        :is-active="false"
        size="s"
        :text="name"
      />
      <h4 :class="$style.name">{{ company }}</h4>
      <p v-if="address">{{ address }}</p>
      <p v-if="street && city">
        {{ street }}<br />
        {{ city }}<span v-if="country">,</span> {{ country }}
      </p>
      <p v-if="additionalText">{{ additionalText }}</p>
      <Button1XP
        v-if="googleMapsUrl"
        start-icon="location"
        :text="googleMapsLabel"
        :url="googleMapsUrl"
        variant="linkStatic"
      />
      <ul :class="$style.contactListing">
        <li v-if="tel">
          <Button1XP start-icon="call" :text="tel" :url="`tel:${tel}`" variant="linkStatic" />
        </li>
        <li v-if="hasMorelInfo">
          <Button1XP
            start-icon="info"
            :text="moreInfoLabel"
            variant="linkStatic"
            @click="openMoreInfo"
          />
        </li>
        <li v-if="jobOpeningsUrl">
          <Button1XP
            start-icon="rocket_launch"
            :text="jobOpeningsLabel"
            :url="jobOpeningsUrl"
            variant="linkStatic"
          />
        </li>
        <li v-if="mobile" v-show="!collapsed">
          <Button1XP
            start-icon="mobile_2"
            :text="mobile"
            :url="`tel:${mobile}`"
            variant="linkStatic"
          />
        </li>
        <li v-if="fax" v-show="!collapsed">
          <Button1XP start-icon="fax" :text="fax" :url="`tel:${mobile}`" variant="linkStatic" />
        </li>
        <li v-if="email" v-show="!collapsed">
          <Button1XP
            start-icon="email"
            :text="email"
            :url="`mailto:${email}`"
            variant="linkStatic"
          />
        </li>
        <li v-if="website" v-show="!collapsed">
          <Button1XP
            start-icon="language"
            :text="websiteText || website"
            :url="website"
            variant="linkStatic"
          />
        </li>
      </ul>
      <template v-if="showCollapsible">
        <button
          :class="[$style.collapsible, !collapsed && $style.expanded]"
          @click="collapsed = !collapsed"
        >
          {{ collapsed ? openLabel : closeLabel }}
        </button>
      </template>
    </template>
    <template v-if="type === 'person'">
      <div :class="$style.mainInfo">
        <div v-if="media" :class="$style.image">
          <template v-if="media.srcSet">
            <img
              :alt="media.alt"
              loading="lazy"
              :srcset="`${media.srcSet.mobile},${media.srcSet.tablet},${media.srcSet.desktop},${media.srcSet.original}`"
            />
          </template>
          <template v-else>
            <img :alt="media.alt" loading="lazy" :src="media.src" />
          </template>
        </div>
        <div :class="$style.personalInfo">
          <h4 :class="$style.name">{{ name }}</h4>
          <p>{{ position }}</p>
        </div>
      </div>
      <ul :class="$style.contactListing">
        <li v-if="tel">
          <Button1XP start-icon="call" :text="tel" :url="`tel:${tel}`" variant="linkStatic" />
        </li>
        <li v-if="mobile">
          <Button1XP
            start-icon="mobile_2"
            :text="mobile"
            :url="`tel:${mobile}`"
            variant="linkStatic"
          />
        </li>
        <li v-if="fax">
          <Button1XP start-icon="fax" :text="fax" :url="`tel:${mobile}`" variant="linkStatic" />
        </li>
        <li v-if="email">
          <Button1XP
            start-icon="email"
            :text="email"
            :url="`mailto:${email}`"
            variant="linkStatic"
          />
        </li>
        <li v-if="vCard">
          <Button1XP
            start-icon="download"
            text="vCard download"
            :url="vCard"
            variant="linkStatic"
          />
        </li>
      </ul>
    </template>
  </div>
</template>
<style lang="sass" module src="./ContactCard.scss" />
